import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';

import Text from '../../../components/Text/Text';
import CustomSelect from '../../../components/Select/CustomSelect';
import { userLogTypeList } from '../../../constants/userLogType';
import { logActionTypeList } from '../../../constants/logActionType';
import Button from '../../../components/Button/Button';
import ButtonTypes from '../../../constants/buttonTypes';
import getLogs from '../../../thunks/logs/getLogs';
import { resetLogsFilter, setLogsFilter } from '../../../actions/logs/logsFilter';
import { authGet } from '../../../utils/request';

const localDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const UserLogFilterMenu = ({ onReset, showUserFilter }) => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const {
    logsFilter: {
      type, action, user, from, to, size
    }
  } = useSelector((state) => ({ logsFilter: state?.logsFilter }), shallowEqual);

  useEffect(() => {
    if (showUserFilter) {
      authGet('/logs/users').then(({ data }) => {
        setUsers(data.map((person) => ({
          value: person.id,
          label: `${`${person.name || ''} ${person.last_name || ''}`.trim() || person.email} (${person.email})`,
        })));
      }).catch(() => setUsers([]));
    }
  }, [showUserFilter]);

  const userOption = users.find((option) => option.value === Number(user)) || null;

  const onApply = () => {
    dispatch(setLogsFilter({ page: 1 }));
    dispatch(getLogs());
  };

  const onClear = () => {
    batch(() => {
      dispatch(resetLogsFilter());
      dispatch(setLogsFilter({ user: showUserFilter ? undefined : user, size }));
      dispatch(getLogs());
    });
    onReset();
  };

  const setPreset = (days) => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - days + 1);
    dispatch(setLogsFilter({ from: localDate(start), to: localDate(today) }));
  };

  return (
    <div className="w-[290px] max-w-full space-y-[12px]">
      <div>
        <Text text="Type" styleContainer="mb-[6px]" textStyle="text-[13px]" />
        <CustomSelect
          value={type || null}
          options={userLogTypeList}
          isClearable
          onChange={(value) => dispatch(setLogsFilter({ type: value }))}
        />
      </div>
      <div>
        <Text text="Action" styleContainer="mb-[6px]" textStyle="text-[13px]" />
        <CustomSelect
          value={action || null}
          options={logActionTypeList}
          isClearable
          onChange={(value) => dispatch(setLogsFilter({ action: value }))}
        />
      </div>
      {showUserFilter && (
        <div>
          <Text text="User" styleContainer="mb-[6px]" textStyle="text-[13px]" />
          <CustomSelect
            value={userOption}
            options={users}
            isClearable
            isSearchable
            onChange={(value) => dispatch(setLogsFilter({ user: value?.value }))}
          />
        </div>
      )}
      <div>
        <Text text="Period" styleContainer="mb-[6px]" textStyle="text-[13px]" />
        <div className="flex gap-[6px] mb-[8px]">
          {[1, 7, 30].map((days) => (
            <button
              key={days}
              type="button"
              className="px-[8px] py-[5px] rounded bg-input-bg text-[12px]"
              onClick={() => setPreset(days)}
            >
              {days === 1 ? 'Today' : `${days} days`}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-[8px]">
          <div>
            <div className="text-[12px]">From</div>
            <input
              aria-label="From date"
              type="date"
              value={from || ''}
              max={to || undefined}
              onChange={(event) => dispatch(setLogsFilter({ from: event.target.value || undefined }))}
              className="w-full p-[7px] rounded bg-input-bg text-dark"
            />
          </div>
          <div>
            <div className="text-[12px]">To</div>
            <input
              aria-label="To date"
              type="date"
              value={to || ''}
              min={from || undefined}
              onChange={(event) => dispatch(setLogsFilter({ to: event.target.value || undefined }))}
              className="w-full p-[7px] rounded bg-input-bg text-dark"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-[10px]">
        <Button text="Reset" type={ButtonTypes.LIGHT} onClick={onClear} />
        <Button text="Apply" onClick={onApply} />
      </div>
    </div>
  );
};

UserLogFilterMenu.propTypes = {
  onReset: PropTypes.func,
  showUserFilter: PropTypes.bool
};

UserLogFilterMenu.defaultProps = {
  onReset: () => {},
  showUserFilter: true
};

export default UserLogFilterMenu;
